/* import __COLOCATED_TEMPLATE__ from './schematic.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { assert } from '@ember/debug';
import Schematic from '@ebiz/aditools-schematic';
import { numberToUserDisplay } from '@ebiz/pretty-num';
import type IntlService from 'ember-intl/services/intl';
import type SiteService from '@ebiz/ember-aditools-site/services/aditools-site';
import type ModelService from '../../services/model';
import type { OpAmpSpec } from '../../services/model';
import type SvgCacheService from '../../services/svg-cache';
import ENV from '../../config/environment';
import { runTask } from 'ember-lifeline';

import * as SpiceSchematicsUtil from '../../utils/spice-schematics';
import HelpTextService from '../../services/help-text';

interface FigureArgs {
  page: string;
  model: ModelService;
  tab: string;
  isPdfFigure: boolean;
}

type SVGPreparedCircuit = ReturnType<typeof Schematic.prepare>;
type SVGString = ReturnType<typeof Schematic.stitchCircuits>;

export default class SchematicFigure extends Component<FigureArgs> {
  @service declare intl: IntlService;
  @service('aditools-site') declare siteconfig: SiteService;
  @service('svg-cache') declare svgCacheService: SvgCacheService;
  @service('help-text') declare helptext: HelpTextService;
  @tracked hasSpares = false;

  @tracked showWhySparesDialog = false;
  @tracked whySparesText: string | undefined;
  @tracked whySparesLoading = false;

  get schematicId(): string {
    return this.args.isPdfFigure ? `pdfschematic-${this.args.page}` : `schematic-${this.args.page}`;
  }

  @action async loadSchematic(element: HTMLElement): Promise<void> {
    const id = element.id;
    const schematicName = SpiceSchematicsUtil.getSchematicName(this.args.page, this.args.model);
    console.debug(`${ENV.rootURL}schematics/${schematicName}.svg`);
    let schematic = await this.svgCacheService.getSVG(`${ENV.rootURL}schematics/${schematicName}.svg`);
    if (!schematic) {
      console.log(`Unable to load schematic from "${ENV.rootURL}schematics/${schematicName}.svg"`);
    } else {
      schematic = this.addSchematicLabels(schematic);
      if (this.args.page === 'nextsteps') {
        const capacitorsAndSpares = await this.buildCapacitorsAndSpares(this.args.model.opAmp1, this.args.model.opAmp2);
        schematic = Schematic.prepare(
          Schematic.composeCircuits(
            [schematic, Schematic.prepare(capacitorsAndSpares.schematic)],
            [
              { x: 0, y: 0 },
              { x: 30, y: 360 },
            ],
          ),
        );
        runTask(
          this,
          () => {
            const svgElement = document.querySelector('.why-spares');
            const url = `${ENV.rootURL}helptext/${this.siteconfig.FullLocale}/why-the-spares.html`;
            svgElement?.addEventListener('click', () => {
              this.whySparesLoading = true;
              this.whySparesText = undefined;
              this.showWhySparesDialog = true;
              this.helptext.helpText(url).then((text) => {
                this.whySparesText = text;
                this.whySparesLoading = false;
              });
            });
          },
          200,
        );
      }
      Schematic.insert(id, schematic);
    }
  }
  @action closeWhySparesDialog(): void {
    this.showWhySparesDialog = false;
  }

  get tooltipType(): string {
    return this.args.page === 'photodiode'
      ? '@equivalent-circuit'
      : this.args.page === 'nextsteps'
        ? '@supply-voltage'
        : '';
  }
  get tooltipText(): string {
    return this.args.page === 'photodiode'
      ? 'figures.schematic.equivalent-circuit-tooltip'
      : this.args.page === 'nextsteps'
        ? 'figures.schematic.supply-voltage-tooltip'
        : '';
  }
  get tooltipQaName(): string {
    return this.args.page === 'photodiode'
      ? 'equivalent_circuit'
      : this.args.page === 'nextsteps'
        ? 'supply_voltage'
        : '';
  }
  get sparesTooltipType(): string {
    return this.args.page === 'nextsteps' && this.hasSpares ? '@why-the-spares' : '';
  }

  get noiseGain(): number {
    const cf = this.args.model.schematicModel.stage1.cf;
    return (this.args.model.state.Cd + cf) / cf;
  }
  get warnAboutNoiseGainUnstable(): boolean {
    return this.args.page === 'circuit' && this.noiseGain < this.args.model.schematicModel.stage1.opAmp.minStableGain;
  }

  get warnAboutLowCf(): boolean {
    return (
      this.args.page === 'circuit' &&
      this.args.model.schematicModel.stage1.cf < 1e-12 &&
      !this.warnAboutNoiseGainUnstable
    );
  }

  addSchematicLabels(schematic: SVGPreparedCircuit): SVGPreparedCircuit {
    const schematicValues: { [item: string]: string } = {};
    const page = this.args.page;
    const state = this.args.model.state;

    // Add labels and values to photodiode Circuit
    const pdModel =
      state.Photodiode && state.Photodiode !== ''
        ? state.Photodiode
        : this.intl.t('figures.schematic.photodiode-model-custom').toString();
    schematicValues['PhotodiodeStageTitle'] = this.intl.t('figures.schematic.photodiode-stage-title-1').toString();
    schematicValues['PhotodiodeStageTitle2'] = this.intl.t('figures.schematic.photodiode-stage-title-2').toString();
    schematicValues['PhotoTitle'] = this.intl.t('figures.schematic.photodiode-model', { pd: pdModel }).toString();
    schematicValues['Vr'] =
      numberToUserDisplay(state.Vr, 'Engineering', 3, { showZeroes: false }) + this.intl.t('units.V').toString();
    schematicValues['Ip'] =
      numberToUserDisplay(state.Ip, 'Engineering', 3, { showZeroes: false }) + this.intl.t('units.A').toString();
    schematicValues['Rsh'] =
      numberToUserDisplay(state.Rsh, 'Engineering', 3, { showZeroes: false }) + this.intl.t('units.R').toString();
    schematicValues['Cd'] =
      numberToUserDisplay(state.Cd, 'Engineering', 3, { showZeroes: false }) + this.intl.t('units.F').toString();
    schematicValues['Vbias'] =
      (state.Bias === '+' || state.Vr === 0 ? '' : '-') +
      numberToUserDisplay(state.Vr, 'Engineering', 3, { showZeroes: false }) +
      this.intl.t('units.V').toString();

    // Fields not used in photodiode (tab 1) schematic - may be overridden later
    schematicValues['Cf'] = '';
    schematicValues['Rf'] = '';
    schematicValues['OpAmp1'] = '';
    schematicValues['OpAmp1Ref'] = '';

    // Stage naming for one stage - may be overridden later
    schematicValues['AmpSingleStageTitle'] = this.intl.t('figures.schematic.amp-single-stage-title-1').toString();
    schematicValues['AmpSingleStageTitle2'] = this.intl.t('figures.schematic.amp-single-stage-title-2').toString();
    schematicValues['AmpSingleStageTitle3'] = this.intl.t('figures.schematic.amp-single-stage-title-3').toString();

    if (page !== 'photodiode') {
      const schematicModel = this.args.model.schematicModel;
      // Stage 1 details
      let linkElem = '<a href="{OpAmp1Url}" target="_blank" class="op-amp-url">{Label}</a>';
      linkElem = linkElem.replace(
        '{OpAmp1Url}',
        `${this.siteconfig['SiteUrl']}/${
          this.siteconfig['AdiLocale']
        }/${schematicModel.stage1.opAmp.generic.toUpperCase()}.html`,
      );
      let opAmp1RefText = '';
      if (page === 'nextsteps') {
        if (this.args.model.opAmp1.numberChannels > 1) {
          linkElem = '1/' + this.args.model.opAmp1.numberChannels.toString() + ' ' + linkElem;
        }
        opAmp1RefText = '{OpAmp1RefDesignator}';
      }
      const opAmp1PartText = linkElem.replace('{Label}', schematicModel.stage1.opAmp.partNumber);
      schematicValues['Cf'] =
        numberToUserDisplay(schematicModel.stage1.cfext, 'Engineering', 3, {
          showZeroes: false,
        }) + this.intl.t('units.F').toString();
      schematicValues['Rf'] =
        numberToUserDisplay(schematicModel.stage1.rf, 'Engineering', 3, {
          showZeroes: false,
        }) + this.intl.t('units.R').toString();
      schematicValues['OpAmp1'] = opAmp1PartText;
      schematicValues['OpAmp1Ref'] = opAmp1RefText;

      if (schematicModel.stage2) {
        // Stage naming with two stages
        schematicValues['AmpStage1Title'] = this.intl.t('figures.schematic.amp-stage-one-title-1').toString();
        schematicValues['AmpStage1Title2'] = this.intl.t('figures.schematic.amp-stage-one-title-2').toString();
        schematicValues['AmpStage1Title3'] = this.intl.t('figures.schematic.amp-stage-one-title-3').toString();
        schematicValues['AmpStage2Title'] = this.intl.t('figures.schematic.amp-stage-two-title-1').toString();
        schematicValues['AmpStage2Title2'] = this.intl.t('figures.schematic.amp-stage-two-title-2').toString();
        schematicValues['AmpStage2Title3'] = this.intl.t('figures.schematic.amp-stage-two-title-3').toString();
        // Stage 2 details
        schematicValues['Rfilter'] =
          numberToUserDisplay(schematicModel.stage2.rfilter, 'Engineering', 3, {
            showZeroes: false,
          }) + this.intl.t('units.R').toString();
        schematicValues['Cfilter'] =
          numberToUserDisplay(schematicModel.stage2.cfilter, 'Engineering', 3, {
            showZeroes: false,
          }) + this.intl.t('units.F').toString();
        schematicValues['Rf2'] =
          numberToUserDisplay(schematicModel.stage2.rf, 'Engineering', 3, {
            showZeroes: false,
          }) + this.intl.t('units.R').toString();
        schematicValues['Rg2'] =
          numberToUserDisplay(schematicModel.stage2.rg, 'Engineering', 3, {
            showZeroes: false,
          }) + this.intl.t('units.R').toString();
        let linkElem2 = '<a href="{OpAmp2Url}" target="_blank" class="op-amp-url">{Label}</a>';
        linkElem2 = linkElem2.replace(
          '{OpAmp2Url}',
          `${this.siteconfig['SiteUrl']}/${
            this.siteconfig['AdiLocale']
          }/${schematicModel.stage2.opAmp.generic.toUpperCase()}.html`,
        );
        let opAmp2RefText = '';
        if (page === 'nextsteps') {
          // this.args.model.opAmp2 will always be truthy when schematicModel.stage2 is truthy, but the type system doesn't know that
          if (this.args.model.opAmp2) {
            if (this.args.model.opAmp2.numberChannels > 1) {
              linkElem2 = '1/' + this.args.model.opAmp2.numberChannels.toString() + ' ' + linkElem2;
            }
          }
          opAmp2RefText = '{OpAmp2RefDesignator}';
        }
        const opAmp2PartText = linkElem2.replace('{Label}', schematicModel.stage2.opAmp.partNumber);
        schematicValues['OpAmp2'] = opAmp2PartText;
        schematicValues['OpAmp2Ref'] = opAmp2RefText;
      }
      // Additional data for next steps diagram
      if (page === 'nextsteps') {
        schematicValues['SupplyVoltageTitle'] = this.intl.t('figures.schematic.supply-voltage-title-1').toString();
        schematicValues['SupplyMinTitle'] = this.intl.t('figures.schematic.supply-voltage-title-2', {
          minVoltage: this.args.model.powerSupplyRange.min
            ? numberToUserDisplay(this.args.model.powerSupplyRange.min, 'Engineering', 3, {
                showZeroes: false,
              }).toString()
            : '',
        });
        schematicValues['SupplyMaxTitle'] = this.intl.t('figures.schematic.supply-voltage-title-3', {
          maxVoltage: numberToUserDisplay(this.args.model.powerSupplyRange.max, 'Engineering', 3, {
            showZeroes: false,
          }).toString(),
        });
        schematicValues['PhotoModel'] = this.intl
          .t('figures.schematic.photodiode-model-next-steps', { pd: pdModel })
          .toString();
        // Search OpAmp Designators
        const bom = this.args.model.bom;
        const partsToSearch = [schematicModel.stage1.opAmp.partNumber];
        if (schematicModel.stage2) partsToSearch.push(schematicModel.stage2.opAmp.partNumber);
        for (const [index, part] of partsToSearch.entries()) {
          for (const item of bom) {
            if (part === item.value) {
              const designators = item.designators;
              const ref = designators.length > 1 ? designators[index] : designators[0];
              schematicValues[index === 0 ? 'OpAmp1RefDesignator' : 'OpAmp2RefDesignator'] = ref as string;
              break;
            }
          }
        }
      }
    }
    return Schematic.substituteCircuitValues(schematic, schematicValues, true);
  }

  async buildCapacitorsAndSpares(
    opAmp1: OpAmpSpec,
    opAmp2: OpAmpSpec | undefined,
  ): Promise<{ schematic: SVGString; width: number; height: number }> {
    assert('buildCapacitorsAndSpares should only be called for nextsteps page', this.args.page === 'nextsteps');
    const schematicModel = this.args.model.schematicModel;
    const schematicsList: SVGPreparedCircuit[] = [];
    const schematicPositions = [];
    let svgWidth = 0;
    // Build Capacitors
    const capacitorSchematicList = [
      {
        schematicFile: `${ENV.rootURL}schematics/bpCapNeg.svg`,
        schematicValues: {
          Cref: 'Cbp+',
          C:
            numberToUserDisplay(0.0000001, 'Engineering', 3, {
              showZeroes: false,
            }) + this.intl.t('units.F').toString(),
        },
        isSecondStageUse: false,
      },
      {
        schematicFile: `${ENV.rootURL}schematics/bpCapPos.svg`,
        schematicValues: {
          Cref: 'Cbp-',
          C:
            numberToUserDisplay(0.0000001, 'Engineering', 3, {
              showZeroes: false,
            }) + this.intl.t('units.F').toString(),
        },
        isSecondStageUse: false,
      },
      {
        schematicFile: `${ENV.rootURL}schematics/bpCapNeg.svg`,
        schematicValues: {
          Cref: 'Cbp2+',
          C:
            numberToUserDisplay(0.0000001, 'Engineering', 3, {
              showZeroes: false,
            }) + this.intl.t('units.F').toString(),
        },
        isSecondStageUse: true,
      },
      {
        schematicFile: `${ENV.rootURL}schematics/bpCapPos.svg`,
        schematicValues: {
          Cref: 'Cbp2-',
          C:
            numberToUserDisplay(0.0000001, 'Engineering', 3, {
              showZeroes: false,
            }) + this.intl.t('units.F').toString(),
        },
        isSecondStageUse: true,
      },
      {
        schematicFile: `${ENV.rootURL}schematics/tantcapNeg.svg`,
        schematicValues: {
          Cref: 'Ctant+',
          C:
            numberToUserDisplay(0.00001, 'Engineering', 3, {
              showZeroes: false,
            }) + this.intl.t('units.F').toString(),
        },
        isSecondStageUse: false,
      },
      {
        schematicFile: `${ENV.rootURL}schematics/tantcapPos.svg`,
        schematicValues: {
          Cref: 'Ctant-',
          C:
            numberToUserDisplay(0.00001, 'Engineering', 3, {
              showZeroes: false,
            }) + this.intl.t('units.F').toString(),
        },
        isSecondStageUse: false,
      },
    ];

    // Let us push the container first
    let xCapacitorPos = 0;
    let container = await this.svgCacheService.getSVG(`${ENV.rootURL}schematics/bpcapContainer.svg`);
    if (!container) {
      console.log(`Unable to load schematic from "${ENV.rootURL}schematics/bpcapContainer.svg"`);
    } else {
      schematicsList.push(
        Schematic.substituteCircuitValues(
          container,
          {
            BypassCapacitorText: this.intl.t('figures.schematic.bypass-capacitors').toString(),
          },
          true,
        ),
      );
      schematicPositions.push({ x: 0, y: 0 });
      // Then push the capacitors
      for (const capacitorSchematic of capacitorSchematicList) {
        if (!capacitorSchematic.isSecondStageUse || schematicModel.stage2) {
          let schematic = await this.svgCacheService.getSVG(capacitorSchematic.schematicFile);
          if (!schematic) {
            console.log(`Unable to load schematic from "${capacitorSchematic.schematicFile}"`);
          } else {
            schematic = Schematic.substituteCircuitValues(schematic, capacitorSchematic.schematicValues, true);
            schematicsList.push(schematic);
            schematicPositions.push({ x: xCapacitorPos, y: 10 });
            xCapacitorPos += 60;
            svgWidth += 60;
          }
        }
      }
    }

    // Build Spares
    const sparesSchematicList = [];
    // Stage 1
    if (opAmp1.numberChannels > 1) {
      let linkElem = `1/${opAmp1.numberChannels.toString()} <a href="{OpAmpUrl}" target="_blank" class="op-amp-url">{Label}</a>`;
      linkElem = linkElem.replace(
        '{OpAmpUrl}',
        `${this.siteconfig['SiteUrl']}/${
          this.siteconfig['AdiLocale']
        }/${schematicModel.stage1.opAmp.generic.toUpperCase()}.html`,
      );
      for (let i = 0; i < opAmp1.numberChannels - 1; i++) {
        // Add Spare OpAmp
        sparesSchematicList.push({
          schematicFile: `${ENV.rootURL}schematics/spare.svg`,
          schematicValues: {
            OpAmp: linkElem.replace('{Label}', schematicModel.stage1.opAmp.partNumber),
            OpAmpRef: 'U1',
          },
        });
      }
    }

    if (schematicModel.stage2) {
      if (schematicModel.stage1.opAmp.partNumber === schematicModel.stage2.opAmp.partNumber) {
        sparesSchematicList.pop();
      } else if (opAmp2 && opAmp2.numberChannels > 1) {
        let linkElem = `1/${opAmp2.numberChannels.toString()} <a href="{OpAmpUrl}" target="_blank" class="op-amp-url">{Label}</a>`;
        linkElem = linkElem.replace(
          '{OpAmpUrl}',
          `${this.siteconfig['SiteUrl']}/${
            this.siteconfig['AdiLocale']
          }/${schematicModel.stage2.opAmp.generic.toUpperCase()}.html`,
        );
        for (let i = 0; i < opAmp2.numberChannels - 1; i++) {
          sparesSchematicList.push({
            schematicFile: `${ENV.rootURL}schematics/spare.svg`,
            schematicValues: {
              OpAmp: linkElem.replace('{Label}', schematicModel.stage2.opAmp.partNumber),
              OpAmpRef: 'U1',
            },
          });
        }
      }
    }
    let xSparePos = xCapacitorPos + 30;
    svgWidth += 30;
    // Let us push the container first (if there are spares)
    container = await this.svgCacheService.getSVG(`${ENV.rootURL}schematics/sparesContainer.svg`);
    if (!container) {
      console.log(`Unable to load schematic from "${ENV.rootURL}schematics/sparesContainer.svg"`);
    } else {
      if (sparesSchematicList.length > 0) {
        schematicsList.push(
          Schematic.substituteCircuitValues(
            container,
            {
              SparesText: this.intl.t('figures.schematic.spares').toString(),
              WhySparesText: this.intl.t('figures.schematic.why-the-spares-tooltip').toString(),
            },
            true,
          ),
        );
        schematicPositions.push({ x: xSparePos, y: 0 });
        this.hasSpares = true;
      }
      // Then push the spares
      for (const sparesSchematic of sparesSchematicList) {
        let schematic = await this.svgCacheService.getSVG(sparesSchematic.schematicFile);
        if (!schematic) {
          console.log(`Unable to load schematic from "${sparesSchematic.schematicFile}"`);
        } else {
          schematic = Schematic.substituteCircuitValues(schematic, sparesSchematic.schematicValues, true);
          schematicsList.push(schematic);
          schematicPositions.push({ x: xSparePos, y: 10 });
          xSparePos += 100;
          svgWidth += 100;
        }
      }
    }
    return {
      schematic: Schematic.composeCircuits(schematicsList, schematicPositions, svgWidth, 140),
      width: svgWidth + 10,
      height: 120,
    };
  }
}
